<template>
  <div>
    <div
      v-if="!entities.length"
      class="text-center p-8 text-neutral-500"
    >
      <p>No asset accounts found.</p>
    </div>

    <div v-else>
      <div class="mb-8">
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <template v-if="hasDefaultCurrency">
            <div class="bg-neutral-200 dark:bg-neutral-800 rounded-lg p-4">
              <div class="text-neutral-500 text-xs uppercase mb-1">
                Assets
              </div>
              <div class="text-green-700 dark:text-green-400 text-xl font-semibold">
                {{ formatAmount(overallSummary.assets, currency) }}
              </div>
            </div>
            <div class="bg-neutral-200 dark:bg-neutral-800 rounded-lg p-4">
              <div class="text-neutral-500 text-xs uppercase mb-1">
                Liabilities
              </div>
              <div class="text-red-700 dark:text-red-400 text-xl font-semibold">
                {{ formatAmount(overallSummary.liabilities, currency) }}
              </div>
            </div>
            <div class="bg-neutral-200 dark:bg-neutral-800 rounded-lg p-4">
              <div class="text-neutral-500 text-xs uppercase mb-1">
                Income
              </div>
              <div class="text-green-700 dark:text-green-400 text-xl font-semibold">
                {{ formatAmount(overallSummary.income, currency) }}
              </div>
            </div>
            <div class="bg-neutral-200 dark:bg-neutral-800 rounded-lg p-4">
              <div class="text-neutral-500 text-xs uppercase mb-1">
                Expenses
              </div>
              <div class="text-red-700 dark:text-red-400 text-xl font-semibold">
                {{ formatAmount(overallSummary.expenses, currency) }}
              </div>
            </div>
            <div class="bg-neutral-200 dark:bg-neutral-800 rounded-lg p-4">
              <div class="text-neutral-500 text-xs uppercase mb-1">
                Net Cash Flow
              </div>
              <div
                :class="overallSummary.netCashFlow >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'"
                class="text-xl font-semibold"
              >
                {{ formatAmount(overallSummary.netCashFlow, currency) }}
              </div>
            </div>
          </template>

          <!-- No default currency chosen: show each currency on its own line rather
               than blend them, and skip Net Cash Flow since it needs a common currency. -->
          <template v-else>
            <div class="bg-neutral-200 dark:bg-neutral-800 rounded-lg p-4">
              <div class="text-neutral-500 text-xs uppercase mb-1">
                Assets
              </div>
              <div
                v-for="(amount, unit) in overallSummary.assetsByUnit"
                :key="`asset-${unit}`"
                class="text-green-700 dark:text-green-400 text-xl font-semibold"
              >
                {{ formatAmount(amount, unit) }}
              </div>
            </div>
            <div class="bg-neutral-200 dark:bg-neutral-800 rounded-lg p-4">
              <div class="text-neutral-500 text-xs uppercase mb-1">
                Liabilities
              </div>
              <div
                v-for="(amount, unit) in overallSummary.liabilitiesByCurrency"
                :key="`liabilities-${unit}`"
                class="text-red-700 dark:text-red-400 text-xl font-semibold"
              >
                {{ formatAmount(amount, unit) }}
              </div>
            </div>
            <div class="bg-neutral-200 dark:bg-neutral-800 rounded-lg p-4">
              <div class="text-neutral-500 text-xs uppercase mb-1">
                Income
              </div>
              <div
                v-for="(amount, unit) in overallSummary.incomeByCurrency"
                :key="`income-${unit}`"
                class="text-green-700 dark:text-green-400 text-xl font-semibold"
              >
                {{ formatAmount(amount, unit) }}
              </div>
            </div>
            <div class="bg-neutral-200 dark:bg-neutral-800 rounded-lg p-4">
              <div class="text-neutral-500 text-xs uppercase mb-1">
                Expenses
              </div>
              <div
                v-for="(amount, unit) in overallSummary.expensesByCurrency"
                :key="`expenses-${unit}`"
                class="text-red-700 dark:text-red-400 text-xl font-semibold"
              >
                {{ formatAmount(amount, unit) }}
              </div>
            </div>
          </template>
        </div>
      </div>

      <div class="space-y-6">
        <div
          v-for="entity in entitySummaries"
          :key="entity.name"
          class="bg-neutral-100 dark:bg-neutral-900 rounded-lg p-4"
        >
          <h4 class="text-neutral-800 dark:text-neutral-200 font-semibold mb-3">
            {{ entity.name }}
          </h4>
          <div class="grid grid-cols-2 lg:grid-cols-3 gap-3 text-sm mb-4">
            <template v-if="hasDefaultCurrency">
              <div>
                <span class="text-neutral-500">Assets:</span>
                <a
                  href="javascript:;"
                  class="text-green-700 dark:text-green-400 ml-2 hover:underline"
                  @click="zoom(entity.name, 'Assets:', 'Assets')"
                >{{ formatAmount(entity.assets, currency) }}</a>
              </div>
              <div>
                <span class="text-neutral-500">Liabilities:</span>
                <a
                  href="javascript:;"
                  class="text-red-700 dark:text-red-400 ml-2 hover:underline"
                  @click="zoom(entity.name, 'Liabilities:', 'Liabilities')"
                >{{ formatAmount(entity.liabilities, currency) }}</a>
              </div>
              <div>
                <span class="text-neutral-500">Income:</span>
                <a
                  href="javascript:;"
                  class="text-green-700 dark:text-green-400 ml-2 hover:underline"
                  @click="zoom(entity.name, 'Income:', 'Income')"
                >{{ formatAmount(entity.income, currency) }}</a>
              </div>
              <div>
                <span class="text-neutral-500">Expenses:</span>
                <a
                  href="javascript:;"
                  class="text-red-700 dark:text-red-400 ml-2 hover:underline"
                  @click="zoom(entity.name, 'Expenses:', 'Expenses')"
                >{{ formatAmount(entity.expenses, currency) }}</a>
              </div>
              <div>
                <span class="text-neutral-500">Cash Flow:</span>
                <span
                  :class="entity.netCashFlow >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'"
                  class="ml-2"
                >{{ formatAmount(entity.netCashFlow, currency) }}</span>
              </div>
            </template>

            <!-- No default currency: one line per currency, no blended Cash Flow -->
            <template v-else>
              <div>
                <span class="text-neutral-500">Assets:</span>
                <a
                  v-for="(amount, unit) in entity.assetsByUnit"
                  :key="`${entity.name}-asset-${unit}`"
                  href="javascript:;"
                  class="text-green-700 dark:text-green-400 ml-2 hover:underline"
                  @click="zoom(entity.name, 'Assets:', 'Assets')"
                >{{ formatAmount(amount, unit) }}</a>
              </div>
              <div>
                <span class="text-neutral-500">Liabilities:</span>
                <a
                  v-for="(amount, unit) in entity.liabilitiesByCurrency"
                  :key="`${entity.name}-liabilities-${unit}`"
                  href="javascript:;"
                  class="text-red-700 dark:text-red-400 ml-2 hover:underline"
                  @click="zoom(entity.name, 'Liabilities:', 'Liabilities')"
                >{{ formatAmount(amount, unit) }}</a>
              </div>
              <div>
                <span class="text-neutral-500">Income:</span>
                <a
                  v-for="(amount, unit) in entity.incomeByCurrency"
                  :key="`${entity.name}-income-${unit}`"
                  href="javascript:;"
                  class="text-green-700 dark:text-green-400 ml-2 hover:underline"
                  @click="zoom(entity.name, 'Income:', 'Income')"
                >{{ formatAmount(amount, unit) }}</a>
              </div>
              <div>
                <span class="text-neutral-500">Expenses:</span>
                <a
                  v-for="(amount, unit) in entity.expensesByCurrency"
                  :key="`${entity.name}-expenses-${unit}`"
                  href="javascript:;"
                  class="text-red-700 dark:text-red-400 ml-2 hover:underline"
                  @click="zoom(entity.name, 'Expenses:', 'Expenses')"
                >{{ formatAmount(amount, unit) }}</a>
              </div>
            </template>
          </div>

          <!-- Yearly Breakdown -->
          <div
            v-if="entity.yearlyData.length > 0"
            class="mt-4 border-t border-neutral-300 dark:border-neutral-800 pt-4"
          >
            <table class="w-full text-sm">
              <thead>
                <tr class="text-neutral-500">
                  <th class="text-left py-1">
                    Year
                  </th>
                  <th class="text-right py-1">
                    Income
                  </th>
                  <th class="text-right py-1">
                    Expenses
                  </th>
                  <th class="text-right py-1">
                    Cash Flow
                  </th>
                  <th class="text-right py-1">
                    YoY
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, idx) in entity.yearlyData"
                  :key="`${entity.name}-year-${row.year}-${row.currency}`"
                  class="border-t border-neutral-300 dark:border-neutral-800"
                >
                  <td class="py-1 text-neutral-600 dark:text-neutral-400">
                    {{ row.year }}
                  </td>
                  <td class="py-1 text-right">
                    <a
                      href="javascript:;"
                      class="text-green-700 dark:text-green-400 hover:underline"
                      @click="zoomYear(entity.name, row.year, 'income')"
                    >{{ formatAmount(row.income, row.currency) }}</a>
                  </td>
                  <td class="py-1 text-right">
                    <a
                      href="javascript:;"
                      class="text-red-700 dark:text-red-400 hover:underline"
                      @click="zoomYear(entity.name, row.year, 'expenses')"
                    >{{ formatAmount(row.expenses, row.currency) }}</a>
                  </td>
                  <td
                    class="py-1 text-right"
                    :class="row.netCashFlow >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'"
                  >
                    {{ formatAmount(row.netCashFlow, row.currency) }}
                  </td>
                  <td
                    class="py-1 text-right"
                    :class="canCompareYoY(entity.yearlyData, idx) ? (yoyChange(entity.yearlyData, idx) >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400') : 'text-neutral-500'"
                  >
                    {{ canCompareYoY(entity.yearlyData, idx) ? formatAmount(yoyChange(entity.yearlyData, idx), row.currency) : '—' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatAmount, convertToCurrency } from '@/parse-and-play/utils'

export default {
  props: {
    app: { type: Object, default: null },
    sheet: { type: Object, default: null }
  },

  computed: {
    transactions () {
      // Get all transactions, excluding periodic (generated) ones to avoid duplicates
      const all = this.app?.queries?.findAllTransactions(this.app.identity.id) || []
      let filtered = all.filter(t => !t.periodicName)

      // Apply date filter from sheet
      if (this.sheet?.dateFilter) {
        filtered = filtered.filter(this.sheet.dateFilter.filter())
      }

      return filtered
    },

    // Empty string is a deliberate "no default currency" choice (see
    // Identity) and must stay empty, not fall back to CAD -- only a
    // missing identity altogether falls back.
    currency () {
      return this.app?.identity ? this.app.identity.defaultCurrency : 'CAD'
    },

    hasDefaultCurrency () {
      return !!this.currency
    },

    assetPath () {
      const accountFilter = this.sheet?.filters?.find(f => f.type === 'account')
      if (!accountFilter) return 'Assets' // Default to all assets

      const value = (accountFilter.value || '').replace(/^\*|\*$/g, '').trim()
      if (!value.startsWith('Assets')) return 'Assets'
      return value
    },

    entityLabel () {
      const depth = (this.assetPath || '').split(':').length
      if (depth <= 1) return 'Category'
      if (depth === 2) return 'Property'
      return 'Item'
    },

    // Discover entities from Asset and Income accounts (not expense-only categories)
    discoverableAccounts () {
      const accounts = new Set()

      for (const t of this.transactions) {
        for (const p of t.getPostings()) {
          // Include accounts that start with Assets: or Income:
          if (p.account.startsWith('Assets:') || p.account.startsWith('Income:')) {
            accounts.add(p.account)
          }
        }
      }
      return accounts
    },

    entityData () {
      if (!this.assetPath) return { all: [], leaves: [] }

      // Extract the category filter (e.g., "Real Estate" from "Assets:Real Estate")
      const pathParts = this.assetPath.split(':')
      const category = pathParts.length > 1 ? pathParts.slice(1).join(':') : null

      // Extract entity names from discoverable accounts
      const allEntities = new Set()
      for (const account of this.discoverableAccounts) {
        const parts = account.split(':')
        if (category) {
          // Find entities under a specific category
          const idx = parts.indexOf(category.split(':')[0])
          if (idx >= 0 && parts.length > idx + 1) {
            allEntities.add(parts[idx + 1])
          }
        } else {
          // Top level - get the second part (e.g., "Consulting" from "Assets:Consulting:...")
          if (parts.length > 1) {
            allEntities.add(parts[1])
          }
        }
      }

      const topLevelEntities = [...allEntities].sort()

      const allDisplayEntities = new Set()
      const leafEntities = new Set()

      for (const entity of topLevelEntities) {
        // Find sub-entities
        const subEntities = new Set()
        for (const account of this.discoverableAccounts) {
          if (account.includes(entity)) {
            const parts = account.split(':')
            const entityIdx = parts.indexOf(entity)
            if (entityIdx >= 0 && parts.length > entityIdx + 1) {
              subEntities.add(parts[entityIdx + 1])
            }
          }
        }

        if (subEntities.size <= 1) {
          // No sub-entities or single sub-entity - use this entity
          allDisplayEntities.add(entity)
          leafEntities.add(entity)
        } else {
          // Multiple sub-entities - add parent for display, sub-entities as leaves
          allDisplayEntities.add(entity)
          for (const sub of subEntities) {
            allDisplayEntities.add(sub)
            leafEntities.add(sub)
          }
        }
      }

      // If we filtered to a specific category, include it as a summary
      if (category && allDisplayEntities.size > 0) {
        allDisplayEntities.add(category.split(':').pop())
      }

      return {
        all: [...allDisplayEntities].sort(),
        leaves: [...leafEntities].sort()
      }
    },

    entities () {
      return this.entityData.all
    },

    leafEntities () {
      return this.entityData.leaves
    },

    entitySummaries () {
      return this.entities.map(entity => this.calculateEntitySummary(entity))
    },

    overallSummary () {
      const totals = {
        assets: 0,
        assetsByUnit: {},
        liabilities: 0,
        liabilitiesByCurrency: {},
        income: 0,
        incomeByCurrency: {},
        expenses: 0,
        expensesByCurrency: {}
      }

      const mergeByCurrency = (into, from) => {
        for (const [currency, amount] of Object.entries(from)) {
          into[currency] = (into[currency] || 0) + amount
        }
      }

      // Only sum leaf entities to avoid double-counting
      for (const entity of this.leafEntities) {
        const summary = this.calculateEntitySummary(entity)
        mergeByCurrency(totals.assetsByUnit, summary.assetsByUnit)
        mergeByCurrency(totals.liabilitiesByCurrency, summary.liabilitiesByCurrency)
        mergeByCurrency(totals.incomeByCurrency, summary.incomeByCurrency)
        mergeByCurrency(totals.expensesByCurrency, summary.expensesByCurrency)
        totals.assets += summary.assets
        totals.liabilities += summary.liabilities
        totals.income += summary.income
        totals.expenses += summary.expenses
      }

      return {
        ...totals,
        netCashFlow: totals.income - totals.expenses
      }
    }
  },

  methods: {
    formatAmount,

    zoom (entity, accountPrefix, label) {
      const transactions = this.transactions.filter(t =>
        t.getPostings().some(p =>
          p.account.includes(entity) && p.account.startsWith(accountPrefix)
        )
      )
      this.app.zoom(`${entity} - ${label}`, transactions)
    },

    zoomYear (entity, year, type) {
      const transactions = this.transactions.filter(t => {
        if (Math.floor(t.date / 10000) !== year) return false
        const prefix = type === 'income' ? 'Income:' : 'Expenses:'
        return t.getPostings().some(p =>
          p.account.includes(entity) && p.account.startsWith(prefix)
        )
      })
      this.app.zoom(`${entity} - ${year} ${type}`, transactions)
    },

    canCompareYoY (yearlyData, idx) {
      if (idx === 0) return false
      const current = yearlyData[idx]
      const previous = yearlyData[idx - 1]
      // Can only compare if same currency and consecutive years
      return current.currency === previous.currency && current.year === previous.year + 1
    },

    yoyChange (yearlyData, idx) {
      return yearlyData[idx].netCashFlow - yearlyData[idx - 1].netCashFlow
    },

    calculateEntitySummary (entity) {
      const assetsByUnit = {}
      const liabilitiesByCurrency = {}
      const incomeByCurrency = {}
      const expensesByCurrency = {}
      let assets = 0
      let liabilities = 0
      let income = 0
      let expenses = 0
      const yearlyData = {} // { "2024:CAD": { income, expenses }, "2024:USD": { ... } }
      const defaultCurrency = this.currency

      for (const t of this.transactions) {
        for (const p of t.getPostings()) {
          if (!p.account.includes(entity)) continue

          const year = Math.floor(t.date / 10000)
          const currency = p.currency === '$' ? defaultCurrency : p.currency
          const converted = defaultCurrency
            ? convertToCurrency(currency === p.currency ? p : { ...p, currency }, defaultCurrency)
            : null

          if (p.account.startsWith('Assets:')) {
            const unit = p.currency || 'units'
            assetsByUnit[unit] = (assetsByUnit[unit] || 0) + (p.amount || 0)
            if (converted !== null) {
              assets += converted
            }
          } else if (p.account.startsWith('Liabilities:')) {
            liabilitiesByCurrency[currency] = (liabilitiesByCurrency[currency] || 0) + (p.amount || 0)
            if (converted !== null) {
              liabilities += converted
            }
          } else if (p.account.startsWith('Income:')) {
            const amt = p.amount || 0
            incomeByCurrency[currency] = (incomeByCurrency[currency] || 0) + amt
            if (converted !== null) {
              income += converted
            }
            const key = `${year}:${currency}`
            if (!yearlyData[key]) yearlyData[key] = { year, currency, income: 0, expenses: 0 }
            yearlyData[key].income += amt
          } else if (p.account.startsWith('Expenses:')) {
            const amt = p.amount || 0
            expensesByCurrency[currency] = (expensesByCurrency[currency] || 0) + amt
            if (converted !== null) {
              expenses += converted
            }
            const key = `${year}:${currency}`
            if (!yearlyData[key]) yearlyData[key] = { year, currency, income: 0, expenses: 0 }
            yearlyData[key].expenses += amt
          }
        }
      }

      // Convert yearly data to sorted array (by year, then currency)
      const yearlyArray = Object.values(yearlyData)
        .map(data => ({
          year: data.year,
          currency: data.currency,
          income: Math.abs(data.income),
          expenses: data.expenses,
          netCashFlow: Math.abs(data.income) - data.expenses
        }))
        .sort((a, b) => a.year - b.year || a.currency.localeCompare(b.currency))

      // Apply abs to each per-currency total
      const abs = (byCurrency) => Object.fromEntries(
        Object.entries(byCurrency).map(([currency, amount]) => [currency, Math.abs(amount)])
      )

      return {
        name: entity,
        assets: Math.abs(assets),
        assetsByUnit: abs(assetsByUnit),
        liabilities: Math.abs(liabilities),
        liabilitiesByCurrency: abs(liabilitiesByCurrency),
        income: Math.abs(income),
        incomeByCurrency: abs(incomeByCurrency),
        expenses,
        expensesByCurrency: abs(expensesByCurrency),
        netCashFlow: Math.abs(income) - expenses,
        yearlyData: yearlyArray
      }
    }
  }
}
</script>
